import React, { ReactElement } from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import { SelectOption } from "../../../common/types"
import PaymentStatus from "../../../payment/status.component"
import IntlProps from "../../../state/helpers/IntlProps"
import { BankDetails, CreditCardDetails, PAYMENT_STATUS, PAYMENT_TYPE } from "../../../state/helpers/Payment"
import { REFUND_TYPE } from "../../../state/invoice/Constants"
import PaymentTypeLabel from "../PaymentTypeLabel/PaymentTypeLabel"
import "./RefundConfirmationTable.scss"

export interface RefundConfirmationRow {
    refundPaymentId: string,
    refundPaymentIdentifier: string,
    currency: string,
    refundedAmount: string,
    refundedMethodType: string,
    refundedPaymentId?: string,
    refundedPaymentIdentifier?: string,
    creditCardDetails?: CreditCardDetails,
    bankAccountDetails?: BankDetails,
    paymentType?: string,
    integrationKey?: string,
    status: string,
}

type OwnProps = {
    title: ReactElement,
    refundType: REFUND_TYPE,
    rows: RefundConfirmationRow[],
    refundMethods: SelectOption<string>[],
}

const RefundConfirmationTable = (props: OwnProps & IntlProps) => {
    return (
        <div className="RefundConfirmationTable">
            <h5>
                {props.title}
            </h5>
            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            <th scope="col" className="payment-col">
                                <FormattedMessage
                                    id="invoice.refund_confirmation_modal.column_header.payment"
                                    defaultMessage="Refunded Payment"
                                />
                            </th>
                            <th scope="col" className="payment-method-col">
                                <FormattedMessage
                                    id="invoice.refund_confirmation_modal.column_header.payment_method"
                                    defaultMessage="Original Payment Method"
                                />
                            </th>
                            <th scope="col" className="refund-col">
                                <FormattedMessage
                                    id="invoice.refund_confirmation_modal.column_header.amount_refunded"
                                    defaultMessage="Amount Refunded"
                                />
                            </th>
                            <th scope="col" className="refund-method-col">
                                <FormattedMessage
                                    id="invoice.refund_confirmation_modal.column_header.refunded_method"
                                    defaultMessage="Refund Method"
                                />
                            </th>
                            <th scope="col" className="refund-payment-col">
                                {props.refundType === REFUND_TYPE.REFUND_ACCOUNT ? (
                                    <FormattedMessage
                                        id="invoice.refund_confirmation_modal.column_header.credit_memo"
                                        defaultMessage="Credit memo"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="invoice.refund_confirmation_modal.column_header.refund_payment"
                                        defaultMessage="Refund"
                                    />
                                )}
                            </th>
                            <th scope="col" className="refund-col">
                                <FormattedMessage
                                    id="invoice.refund_confirmation_modal.column_header.refund_status"
                                    defaultMessage="Refund Status"
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.rows.map(row => (
                            <PaymentRow
                                key={row.refundPaymentId}
                                row={row}
                                refundMethods={props.refundMethods}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

type PaymentRowProps = {
    row: RefundConfirmationRow,
    refundMethods: SelectOption<string>[],
}

const PaymentRow = injectIntl((props: PaymentRowProps & IntlProps) => {
    const getRefundMethodTypeMessage = (refundType: string) => {
        switch (refundType) {
        case PAYMENT_TYPE.CREDIT_CARD:
            return <FormattedMessage
                id="refund_confirmation_modal.refund_method_type.credit_card"
                defaultMessage="Credit Card"
            />
        case PAYMENT_TYPE.BANK_TRANSFER:
            return <FormattedMessage
                id="refund_confirmation_modal.refund_method_type.bank_transfer"
                defaultMessage="Bank Transfer"
            />
        case PAYMENT_TYPE.CHECK:
            return <FormattedMessage
                id="refund_confirmation_modal.refund_method_type.check"
                defaultMessage="Check"
            />
        case PAYMENT_TYPE.CASH:
            return <FormattedMessage
                id="refund_confirmation_modal.refund_method_type.cash"
                defaultMessage="Cash"
            />
        case PAYMENT_TYPE.CREDIT:
            return <FormattedMessage
                id="refund_confirmation_modal.refund_method_type.on_account"
                defaultMessage="On Account"
            />
        default:
            return <span>{refundType}</span>
        }
    }
    
    return (
        <tr>
            <td className={props.row.refundedPaymentId ? "" : "align-center"}>
                {props.row.refundedPaymentId ? (
                    <a
                        href={`/payments/${props.row.refundedPaymentId}/edit`}
                        target="_blank"
                        rel="noreferrer"
                    >{props.row.refundedPaymentIdentifier}</a>
                ) : (
                    "-"
                )}
            </td>
            <td className={props.row.refundedPaymentId ? "" : "align-center"}>
                {props.row.refundedPaymentId ? (
                    <PaymentTypeLabel
                        creditCardDetails={props.row.creditCardDetails}
                        bankAccountDetails={props.row.bankAccountDetails}
                        paymentType={props.row.paymentType}
                        integrationKey={props.row.integrationKey}
                    />
                ) : (
                    "-"
                )}
            </td>
            <td>
                {![PAYMENT_STATUS.DECLINED].includes(props.row.status) ?
                    props.row.refundedAmount : "-"
                }
            </td>
            <td>
                {getRefundMethodTypeMessage(props.row.refundedMethodType)}
            </td>
            <td>
                <a
                    href={`/payments/${props.row.refundPaymentId}/edit`}
                    target="_blank"
                    rel="noreferrer"
                >{props.row.refundPaymentIdentifier}</a>
            </td>
            <td>
                <PaymentStatus value={props.row.status} />
            </td>
        </tr>
    )
})

export default injectIntl(RefundConfirmationTable)



// WEBPACK FOOTER //
// ./src/invoice/components/RefundConfirmationTable/RefundConfirmationTable.tsx